import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Target, Plus, Pencil, Trash2, Power, ShieldCheck, Mail,
} from "lucide-react";
import DateInputBR from "@/components/DateInputBR";
import { toast } from "sonner";

const INDICATORS = [
  { key: "turnover",    label: "Turnover",    unit: "%",   defaultOp: "<=" },
  { key: "absenteismo", label: "Absenteísmo", unit: "%",   defaultOp: "<=" },
  { key: "headcount",   label: "Headcount",   unit: "qtd", defaultOp: "<=" },
  { key: "horas_extras",label: "Horas Extras",unit: "h",   defaultOp: "<=" },
];

const SCOPE_LABEL = { grupo: "Grupo", empresa: "Empresa", setor: "Setor" };

export default function GoalsAdminPage() {
  const { user } = useAuth();
  const { companies } = useCompany();
  const isMaster = user?.role === "master";
  const isAdmin  = user?.role === "rh_admin" || isMaster;

  const [goals, setGoals] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const [recipDialog, setRecipDialog] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: g }, { data: s }] = await Promise.all([
        api.get("/dashboard/goals", { params: { include_inactive: true } }),
        api.get("/sectors").catch(() => ({ data: [] })),
      ]);
      setGoals(g.goals || []);
      setSectors(Array.isArray(s) ? s : (s?.sectors || []));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (g) => {
    const gid = `${g.indicator}:${g.scope}:${g.company_id || "-"}:${g.sector_id || "-"}`;
    if (!window.confirm(`Excluir a meta de ${INDICATORS.find(i => i.key === g.indicator)?.label || g.indicator}?`)) return;
    try {
      await api.delete(`/dashboard/goals/${encodeURIComponent(gid)}`);
      toast.success("Meta excluída.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (g) => {
    // Reutiliza upsert com active invertido
    try {
      await api.post("/dashboard/goals", {
        indicator: g.indicator, target: g.target, operator: g.operator,
        unit: g.unit, scope: g.scope, company_id: g.company_id, sector_id: g.sector_id,
        active: !g.active, description: g.description,
      });
      toast.success(g.active ? "Meta inativada." : "Meta ativada.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!isAdmin) {
    return (
      <div className="max-w-xl mx-auto py-16 text-center" data-testid="goals-forbidden">
        <ShieldCheck className="h-8 w-8 mx-auto text-slate-300 mb-3" />
        <h1 className="font-display text-xl font-semibold">Acesso restrito</h1>
        <p className="text-[13px] text-muted-foreground mt-1">
          Apenas usuários com perfil <b>Master</b> ou <b>RH Admin</b> podem configurar metas.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8" data-testid="goals-admin-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">
            Centro de Inteligência de RH
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Metas do Grupo</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Configure metas de Turnover, Absenteísmo, Headcount e Horas Extras. As metas alimentam
            os semáforos e o painel <b>Metas × Realizado</b> do Dashboard.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm"
                  onClick={() => setRecipDialog(true)}
                  data-testid="btn-recipients">
            <Mail className="h-4 w-4 mr-1.5" /> Destinatários de alertas
          </Button>
          <Button size="sm"
                  className="bg-[#065F46] hover:bg-[#064E3B] text-white"
                  onClick={() => setDialog({ open: true, editing: null })}
                  data-testid="btn-new-goal">
            <Plus className="h-4 w-4 mr-1.5" /> Nova meta
          </Button>
        </div>
      </header>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10.5px] uppercase tracking-[0.14em] text-slate-500">
              <tr>
                <th className="px-4 py-2.5 text-left">Indicador</th>
                <th className="px-4 py-2.5 text-left">Escopo</th>
                <th className="px-4 py-2.5 text-left">Empresa / Setor</th>
                <th className="px-4 py-2.5 text-right">Meta</th>
                <th className="px-4 py-2.5 text-left">Vigência</th>
                <th className="px-4 py-2.5 text-center">Status</th>
                <th className="px-4 py-2.5 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td colSpan={7} className="p-3"><Skeleton className="h-8" /></td>
                  </tr>
                ))
              ) : goals.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-slate-500 text-[13px]">
                    Nenhuma meta cadastrada. Clique em <b>Nova meta</b> para começar.
                  </td>
                </tr>
              ) : goals.map((g) => {
                const ind = INDICATORS.find((i) => i.key === g.indicator);
                const comp = companies?.find((c) => c.id === g.company_id);
                const sec = sectors.find((s) => s.id === g.sector_id);
                const scopeCanEdit = isMaster || (g.scope !== "grupo" && (user?.company_ids || []).includes(g.company_id));
                return (
                  <tr key={`${g.indicator}:${g.scope}:${g.company_id || "-"}:${g.sector_id || "-"}`}
                      className="border-t border-slate-100 hover:bg-slate-50/60"
                      data-testid={`goal-row-${g.indicator}-${g.scope}`}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-slate-900">{ind?.label || g.indicator}</div>
                      {g.description && <div className="text-[11px] text-slate-500 truncate max-w-[220px]">{g.description}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="outline" className="text-[10.5px] border-slate-300 text-slate-700">
                        {SCOPE_LABEL[g.scope] || g.scope}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-slate-700">
                      {g.scope === "grupo" ? "—" : (
                        <>
                          {comp?.nome_fantasia || comp?.razao_social || "?"}
                          {sec ? <span className="text-slate-500"> · {sec.name}</span> : null}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-slate-900 tabular-nums">
                      {g.operator} {g.target}{g.unit === "%" ? "%" : ` ${g.unit || ""}`.trimEnd()}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-[12px]">
                      {g.period_start ? g.period_start : "—"}
                      {g.period_end ? ` → ${g.period_end}` : ""}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge className={`text-[10.5px] ${g.active
                        ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                        : "bg-slate-100 text-slate-600 border-slate-200"}`}
                        variant="outline">
                        {g.active ? "Ativa" : "Inativa"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => toggleActive(g)}
                              disabled={!scopeCanEdit}
                              title={g.active ? "Inativar" : "Ativar"}
                              data-testid={`btn-toggle-${g.indicator}`}>
                        <Power className={`h-3.5 w-3.5 ${g.active ? "text-emerald-600" : "text-slate-400"}`} />
                      </Button>
                      <Button size="sm" variant="ghost"
                              onClick={() => setDialog({ open: true, editing: g })}
                              disabled={!scopeCanEdit}
                              data-testid={`btn-edit-${g.indicator}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(g)}
                              disabled={!scopeCanEdit}
                              className="text-rose-600 hover:bg-rose-50"
                              data-testid={`btn-del-${g.indicator}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {dialog.open && (
        <GoalDialog
          goal={dialog.editing}
          companies={companies || []}
          sectors={sectors}
          isMaster={isMaster}
          onClose={() => setDialog({ open: false, editing: null })}
          onSaved={() => { setDialog({ open: false, editing: null }); load(); }}
        />
      )}
      {recipDialog && (
        <RecipientsDialog
          companies={companies || []}
          isMaster={isMaster}
          onClose={() => setRecipDialog(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function GoalDialog({ goal, companies, sectors, isMaster, onClose, onSaved }) {
  const isEditing = !!goal;
  const [form, setForm] = useState(() => ({
    indicator:   goal?.indicator   || "turnover",
    scope:       goal?.scope       || (isMaster ? "grupo" : "empresa"),
    company_id:  goal?.company_id  || null,
    sector_id:   goal?.sector_id   || null,
    target:      goal?.target      ?? 3,
    operator:    goal?.operator    || "<=",
    unit:        goal?.unit        || "%",
    period_start:goal?.period_start|| "",
    period_end:  goal?.period_end  || "",
    active:      goal?.active !== false,
    description: goal?.description || "",
  }));
  const [saving, setSaving] = useState(false);

  const sectorsOfCompany = useMemo(
    () => (sectors || []).filter((s) => !form.company_id || s.company_id === form.company_id),
    [sectors, form.company_id]
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (form.scope === "empresa" && !form.company_id) { toast.error("Selecione a empresa."); return; }
    if (form.scope === "setor" && (!form.company_id || !form.sector_id)) {
      toast.error("Selecione empresa e setor."); return;
    }
    // Confirmação — meta já vinculada ao Dashboard
    if (isEditing && !window.confirm(
      "Alterar esta meta atualizará imediatamente os semáforos e o painel Metas × Realizado. Deseja continuar?"
    )) return;
    setSaving(true);
    try {
      const payload = {
        indicator: form.indicator,
        target: Number(form.target),
        operator: form.operator,
        unit: form.unit,
        scope: form.scope,
        company_id: form.scope === "grupo" ? null : form.company_id,
        sector_id:  form.scope === "setor"  ? form.sector_id : null,
        period_start: form.period_start || null,
        period_end: form.period_end || null,
        active: form.active,
        description: form.description || null,
      };
      await api.post("/dashboard/goals", payload);
      toast.success(isEditing ? "Meta atualizada." : "Meta criada.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="goal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-4 w-4 text-[#065F46]" />
            {isEditing ? "Editar meta" : "Nova meta"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label className="text-[11px] uppercase tracking-wider">Indicador *</Label>
            <Select value={form.indicator} onValueChange={(v) => {
              const ind = INDICATORS.find(i => i.key === v);
              set("indicator", v);
              set("unit", ind?.unit || "%");
              set("operator", ind?.defaultOp || "<=");
            }}>
              <SelectTrigger data-testid="goal-indicator"><SelectValue /></SelectTrigger>
              <SelectContent>
                {INDICATORS.map((i) => (
                  <SelectItem key={i.key} value={i.key}>{i.label} ({i.unit})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider">Escopo *</Label>
            <Select value={form.scope} onValueChange={(v) => {
              set("scope", v);
              if (v === "grupo") { set("company_id", null); set("sector_id", null); }
              if (v === "empresa") { set("sector_id", null); }
            }}>
              <SelectTrigger data-testid="goal-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                {isMaster && <SelectItem value="grupo">Grupo</SelectItem>}
                <SelectItem value="empresa">Empresa</SelectItem>
                <SelectItem value="setor">Setor</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider">Empresa</Label>
            <Select disabled={form.scope === "grupo"} value={form.company_id || ""}
                    onValueChange={(v) => { set("company_id", v || null); set("sector_id", null); }}>
              <SelectTrigger data-testid="goal-company"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.scope === "setor" && (
            <div className="col-span-2">
              <Label className="text-[11px] uppercase tracking-wider">Setor</Label>
              <Select value={form.sector_id || ""} onValueChange={(v) => set("sector_id", v)}>
                <SelectTrigger data-testid="goal-sector"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                <SelectContent>
                  {sectorsOfCompany.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} {s.code ? `(${s.code})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-[11px] uppercase tracking-wider">Comparação</Label>
            <Select value={form.operator} onValueChange={(v) => set("operator", v)}>
              <SelectTrigger data-testid="goal-operator"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="<=">≤ (menor ou igual)</SelectItem>
                <SelectItem value=">=">≥ (maior ou igual)</SelectItem>
                <SelectItem value="==">= (igual)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider">Meta *</Label>
            <div className="flex gap-2 items-center">
              <Input type="number" step="0.01" value={form.target}
                     onChange={(e) => set("target", e.target.value)}
                     data-testid="goal-target" />
              <span className="text-[13px] text-slate-500 font-medium w-8">{form.unit}</span>
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider">Vigência início</Label>
            <DateInputBR value={form.period_start}
                   onChange={(v) => set("period_start", v)}
                   testId="goal-start" />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Vigência fim</Label>
            <DateInputBR value={form.period_end}
                   onChange={(v) => set("period_end", v)}
                   testId="goal-end" />
          </div>

          <div className="col-span-2">
            <Label className="text-[11px] uppercase tracking-wider">Descrição</Label>
            <Input value={form.description} onChange={(e) => set("description", e.target.value)}
                   placeholder="Opcional — contexto da meta"
                   data-testid="goal-desc" />
          </div>

          <label className="col-span-2 flex items-center gap-2 text-[12.5px]">
            <Switch checked={form.active} onCheckedChange={(v) => set("active", v)}
                    data-testid="goal-active" />
            Meta ativa (usa no cálculo do Dashboard)
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving}
                  className="bg-[#065F46] hover:bg-[#064E3B] text-white"
                  data-testid="goal-save">
            {saving ? "Salvando..." : (isEditing ? "Salvar alterações" : "Criar meta")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
function RecipientsDialog({ companies, isMaster, onClose }) {
  const [companyId, setCompanyId] = useState(null); // null = grupo
  const [form, setForm] = useState({ turnover: "", headcount: "", absenteismo: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async (cid) => {
    setLoading(true);
    try {
      const params = cid ? { company_id: cid } : {};
      const { data } = await api.get("/dashboard/alert-recipients", { params });
      setForm({
        turnover:    (data.turnover    || []).join(", "),
        headcount:   (data.headcount   || []).join(", "),
        absenteismo: (data.absenteismo || []).join(", "),
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(companyId); }, [companyId]);

  const save = async () => {
    setSaving(true);
    try {
      const toList = (s) => s.split(",").map((x) => x.trim()).filter((x) => x.includes("@"));
      await api.put("/dashboard/alert-recipients", {
        turnover: toList(form.turnover),
        headcount: toList(form.headcount),
        absenteismo: toList(form.absenteismo),
        company_id: companyId,
      });
      toast.success("Destinatários salvos.");
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="recipients-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-[#065F46]" /> Destinatários dos alertas de RH
          </DialogTitle>
        </DialogHeader>
        <p className="text-[12px] text-slate-500">
          E-mails separados por vírgula. Alertas são disparados apenas na transição
          <b> Normal → Crítico</b> (idempotente).
        </p>

        <div className="mb-2">
          <Label className="text-[11px] uppercase tracking-wider">Escopo</Label>
          <Select value={companyId || "grupo"} onValueChange={(v) => setCompanyId(v === "grupo" ? null : v)}>
            <SelectTrigger data-testid="recip-scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              {isMaster && <SelectItem value="grupo">Grupo (padrão)</SelectItem>}
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? <Skeleton className="h-32" /> : (
          <div className="space-y-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Turnover</Label>
              <Input value={form.turnover}
                     onChange={(e) => setForm({ ...form, turnover: e.target.value })}
                     placeholder="rh@empresa.com, diretor@empresa.com"
                     data-testid="recip-turnover" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Headcount</Label>
              <Input value={form.headcount}
                     onChange={(e) => setForm({ ...form, headcount: e.target.value })}
                     placeholder="rh@empresa.com"
                     data-testid="recip-headcount" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Absenteísmo</Label>
              <Input value={form.absenteismo}
                     onChange={(e) => setForm({ ...form, absenteismo: e.target.value })}
                     placeholder="rh@empresa.com"
                     data-testid="recip-absenteismo" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving}
                  className="bg-[#065F46] hover:bg-[#064E3B] text-white"
                  data-testid="recip-save">
            {saving ? "Salvando..." : "Salvar destinatários"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
